/**
 * API Service - Axios instance with interceptors
 * For Media Devoted | Created By Ciro Autiero
 *
 * Auth model (senior-level, no surprise logouts):
 *   - Access token is short-lived (e.g. 15m). The client NEVER logs the user
 *     out just because the access token expired: it silently refreshes using
 *     the refresh token.
 *   - Refresh token is long-lived (7d, or 30d with "Remember me"). Every
 *     refresh rotates both tokens so an active session effectively renews
 *     forever.
 *   - A proactive timer refreshes ~60s before access-token expiry so API
 *     calls always go out with a fresh token. As a safety net, 401 responses
 *     also trigger a single refresh + retry.
 *   - The ONLY paths to a forced logout are (a) the backend rejects the
 *     refresh token, or (b) there is no refresh token at all.
 *   - Multiple tabs stay in sync via the `storage` event.
 */

import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

export const TOKEN_KEY = 'trafficpulse-token';
export const REFRESH_KEY = 'trafficpulse-refresh';
export const TOKEN_EXPIRY_KEY = 'trafficpulse-token-expiry';
const CORRELATION_ID_KEY = 'trafficpulse-correlation-id';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:4000';

/**
 * How far ahead of expiry we start silently refreshing. Must be smaller than
 * the shortest access-token TTL we issue (15 minutes).
 */
const REFRESH_LEAD_MS = 60 * 1000;

/**
 * Minimum delay for the proactive refresh timer. Avoids busy loops if the
 * token is already close to expired for any reason.
 */
const MIN_REFRESH_DELAY_MS = 1000;

export const api = axios.create({
  baseURL: `${API_BASE}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 45_000,
});

// ---------------------------------------------------------------------------
// Correlation id
// ---------------------------------------------------------------------------

function generateCorrelationId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `cid-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function getCorrelationId(): string {
  const storage = typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  const existing = storage?.getItem(CORRELATION_ID_KEY);
  if (existing) return existing;

  const correlationId = generateCorrelationId();
  storage?.setItem(CORRELATION_ID_KEY, correlationId);
  return correlationId;
}

// ---------------------------------------------------------------------------
// Token storage
// ---------------------------------------------------------------------------

interface RefreshResponseData {
  accessToken: string;
  refreshToken: string;
  expiresIn?: number;
}

/**
 * Store a new token pair. If the backend (or a caller) forgets to send a new
 * refresh token, we preserve the existing one rather than writing `undefined`
 * into localStorage, which used to silently break the session after a refresh.
 */
export const setAuthTokens = (
  accessToken: string,
  refreshToken: string | null | undefined,
  expiresIn: number,
) => {
  const expiryTime = Date.now() + expiresIn * 1000;
  localStorage.setItem(TOKEN_KEY, accessToken);
  if (refreshToken) {
    localStorage.setItem(REFRESH_KEY, refreshToken);
  }
  localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());

  api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  scheduleProactiveRefresh(expiryTime);

  window.dispatchEvent(
    new CustomEvent('trafficpulse:tokens-updated', { detail: { expiryTime } }),
  );
};

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getApiBaseUrl(): string {
  return API_BASE;
}

/**
 * Wipe everything and redirect to /login. The single source of truth for
 * "session is over" — whether that was triggered by an explicit logout or by
 * a refresh-token rejection.
 */
export const clearAuthAndRedirect = () => {
  cancelProactiveRefresh();
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  delete api.defaults.headers.common.Authorization;
  window.dispatchEvent(new Event('trafficpulse:auth-cleared'));
  if (!window.location.pathname.includes('/login')) {
    window.location.href = '/login';
  }
};

// ---------------------------------------------------------------------------
// Refresh orchestration
// ---------------------------------------------------------------------------

let refreshPromise: Promise<string> | null = null;
let refreshTimer: number | null = null;
let failedQueue: {
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}[] = [];

const processQueue = (error: unknown, token: string | null) => {
  failedQueue.forEach((p) => {
    if (error) p.reject(error);
    else p.resolve(token as string);
  });
  failedQueue = [];
};

function cancelProactiveRefresh() {
  if (refreshTimer !== null) {
    window.clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function scheduleProactiveRefresh(expiryTimeMs: number) {
  cancelProactiveRefresh();
  const delay = Math.max(
    MIN_REFRESH_DELAY_MS,
    expiryTimeMs - Date.now() - REFRESH_LEAD_MS,
  );
  refreshTimer = window.setTimeout(() => {
    void refreshAccessToken().catch(() => {
      /* handled inside refreshAccessToken */
    });
  }, delay);
}

/**
 * Single-flight refresh. Concurrent callers share the same promise and the
 * response interceptor's queue is drained only after it resolves.
 */
async function refreshAccessToken(): Promise<string> {
  if (refreshPromise) return refreshPromise;

  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) {
    clearAuthAndRedirect();
    throw new Error('No refresh token');
  }

  refreshPromise = (async () => {
    try {
      const response = await axios.post<{ data: RefreshResponseData }>(
        `${API_BASE}/api/auth/refresh`,
        { refreshToken },
        {
          timeout: 15_000,
          headers: { 'Content-Type': 'application/json' },
        },
      );
      const payload = response.data?.data;
      if (!payload?.accessToken) {
        throw new Error('Malformed refresh response');
      }

      setAuthTokens(
        payload.accessToken,
        payload.refreshToken ?? refreshToken,
        payload.expiresIn ?? 900,
      );
      processQueue(null, payload.accessToken);
      return payload.accessToken;
    } catch (error) {
      processQueue(error, null);
      clearAuthAndRedirect();
      throw error;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Called on bootstrap and after every storage change. Ensures the in-memory
 * axios header and the refresh timer match whatever is in localStorage.
 */
export function bootstrapAuthFromStorage() {
  const token = localStorage.getItem(TOKEN_KEY);
  const expiryStr = localStorage.getItem(TOKEN_EXPIRY_KEY);
  if (token) {
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common.Authorization;
  }
  const expiry = expiryStr ? Number.parseInt(expiryStr, 10) : 0;
  if (token && Number.isFinite(expiry) && expiry > 0) {
    scheduleProactiveRefresh(expiry);
  } else {
    cancelProactiveRefresh();
  }
}

// Sync between tabs. If another tab signs in/out or refreshes, mirror the
// change here so nothing drifts.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (
      event.key === TOKEN_KEY ||
      event.key === TOKEN_EXPIRY_KEY ||
      event.key === null // localStorage.clear()
    ) {
      bootstrapAuthFromStorage();
    }
  });
}

// ---------------------------------------------------------------------------
// Interceptors
// ---------------------------------------------------------------------------

const AUTH_BYPASS = ['/auth/login', '/auth/register', '/auth/refresh'];

function isAuthBypassUrl(url?: string): boolean {
  if (!url) return false;
  return AUTH_BYPASS.some((suffix) => url.includes(suffix));
}

api.interceptors.request.use(
  (config) => {
    config.headers = config.headers ?? {};
    if (
      !config.headers['X-Correlation-Id'] &&
      !config.headers['x-correlation-id']
    ) {
      config.headers['X-Correlation-Id'] = getCorrelationId();
    }

    if (isAuthBypassUrl(config.url)) {
      return config;
    }

    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as
      | (AxiosRequestConfig & { _retry?: boolean })
      | undefined;

    if (
      !originalRequest ||
      error.response?.status !== 401 ||
      originalRequest._retry ||
      isAuthBypassUrl(originalRequest.url)
    ) {
      return Promise.reject(error);
    }

    const refreshToken = localStorage.getItem(REFRESH_KEY);
    if (!refreshToken) {
      clearAuthAndRedirect();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      // If a refresh is already in-flight, queue. Otherwise kick one off.
      const newToken = refreshPromise
        ? await new Promise<string>((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
        : await refreshAccessToken();

      originalRequest.headers = originalRequest.headers ?? {};
      (originalRequest.headers as Record<string, string>).Authorization =
        `Bearer ${newToken}`;
      return api(originalRequest);
    } catch (refreshError) {
      return Promise.reject(refreshError);
    }
  },
);

// Run once at module load so an already-persisted session is armed.
if (typeof window !== 'undefined') {
  bootstrapAuthFromStorage();
}

export default api;
